import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  Tooltip,
  Chip,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axiosConfig';
import { useAuth } from '../AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';

// Icons
import NotificationsIcon from '@mui/icons-material/Notifications';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';

// Tab components
import AlertsList from './AlertsList';
import NotificationsList from './NotificationsList';

// TabPanel component for handling tab content visibility
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`alerts-tabpanel-${index}`}
      aria-labelledby={`alerts-tab-${index}`}
      {...other}
      sx={{ py: 3 }}
    >
      {value === index && children}
    </Box>
  );
}

const AlertManagementDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // States for data
  const [alerts, setAlerts] = useState([]);
  const { notifications, setNotifications } = useNotifications();

  // Alert limit calculations
  const alertLimit = user?.plan?.alertLimit || 5;
  const isUnlimited = alertLimit === -1;
  const currentCount = alerts.length;
  const limitReached = !isUnlimited && currentCount >= alertLimit;

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Load alerts AND notifications on component mount ONLY
  useEffect(() => {
    fetchAlerts();
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only runs once

  // Fetch user's alerts
  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/alerts');
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setErrorMessage('Failed to load your alerts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's notifications - KEEPING THIS
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/notifications');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setErrorMessage('Failed to load your notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for alert toggle
  const handleAlertToggle = async (alertId, enabled) => {
    try {
      await axios.patch(`/api/alerts/${alertId}/toggle`);

      // Update local state
      setAlerts(prevAlerts => prevAlerts.map(alert =>
        alert._id === alertId ? { ...alert, enabled: !alert.enabled } : alert
      ));

      setSuccessMessage(`Alert ${enabled ? 'disabled' : 'enabled'} successfully`);
    } catch (error) {
      console.error('Error toggling alert:', error);
      setErrorMessage('Failed to update alert status. Please try again.');
    }
  };

  // Handler for alert deletion
  const handleAlertDelete = async (alertId) => {
    try {
      await axios.delete(`/api/alerts/${alertId}`);

      // Update local state
      setAlerts(prevAlerts => prevAlerts.filter(alert => alert._id !== alertId));

      setSuccessMessage('Alert deleted successfully');
    } catch (error) {
      console.error('Error deleting alert:', error);
      setErrorMessage('Failed to delete alert. Please try again.');
    }
  };

  // Handler for clearing inactive alerts
  const handleClearInactiveAlerts = async () => {
    try {
      await axios.post('/api/alerts/clear-inactive');

      // Update local state - remove inactive alerts
      const activeAlerts = alerts.filter(alert => alert.enabled);
      setAlerts(activeAlerts);

      setSuccessMessage('Inactive alerts cleared successfully');
    } catch (error) {
      console.error('Error clearing inactive alerts:', error);
      setErrorMessage('Failed to clear inactive alerts. Please try again.');
    }
  };

  // Handler for marking notification as read 
  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.patch(`/api/notifications/${notificationId}/read`);

      // Update local state
      setNotifications(prevNotifications => prevNotifications.map(notification =>
        notification._id === notificationId ? { ...notification, isRead: true } : notification
      ));

    } catch (error) {
      console.error('Error marking notification as read:', error);
      setErrorMessage('Failed to mark notification as read. Please try again.');
    }
  };

  // Handler for marking all notifications as read - KEEPING THIS
  const handleMarkAllAsRead = async () => {
    try {
      await axios.post('/api/notifications/read-all');

      // Update local state
      setNotifications(prevNotifications => prevNotifications.map(notification => ({
        ...notification,
        isRead: true
      })));

      setSuccessMessage('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setErrorMessage('Failed to mark notifications as read. Please try again.');
    }
  };

  // Handler for clearing read notifications - KEEPING THIS
  const handleClearReadNotifications = async () => {
    try {
      await axios.post('/api/notifications/clear-read');

      // Update local state - remove read notifications
      const unreadNotifications = notifications.filter(notification => !notification.isRead);
      setNotifications(unreadNotifications);

      setSuccessMessage('Read notifications cleared successfully');
    } catch (error) {
      console.error('Error clearing read notifications:', error);
      setErrorMessage('Failed to clear read notifications. Please try again.');
    }
  };

  // Handler for creating new alert
  const handleCreateAlert = () => {
    navigate('/alerts/new');
  };

  // Handler for navigating to channel settings
  const handleChannelSettings = () => {
    navigate('/account/channels');
  };

  // Create button content based on screen size and limit status
  const renderCreateButton = () => {
    if (limitReached) {
      return (
        <Tooltip title="Alert limit reached. Delete alerts or upgrade your plan.">
          <span>
            <Button
              variant="contained"
              color="primary"
              startIcon={isMobile ? null : <LockIcon />}
              disabled
              size={isMobile ? "small" : "medium"}
            >
              {isMobile ? <LockIcon fontSize="small" /> : 'Create New Alert'}
            </Button>
          </span>
        </Tooltip>
      );
    }

    return (
      <Button
        variant="contained"
        color="primary"
        startIcon={isMobile ? null : <AddIcon />}
        onClick={handleCreateAlert}
        size={isMobile ? "small" : "medium"}
      >
        {isMobile ? <AddIcon fontSize="small" /> : 'Create New Alert'}
      </Button>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Alerts & Notifications
          <Chip label="Beta" size="small" color="primary" variant="outlined" style={{ marginLeft: 8 }} />
        </Typography>


        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {renderCreateButton()}

          <Tooltip title="Channel Settings">
            <IconButton
              onClick={handleChannelSettings}
              sx={{
                ml: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.05)',
                }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Show limit status if limit is reached */}
      {limitReached && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You've reached your {user?.plan?.name || 'Free'} plan limit of {alertLimit} alerts.
          Delete existing alerts or <a href="/pricing" style={{ color: theme.palette.warning.dark }}>upgrade your plan</a> to create more.
        </Alert>
      )}

      <Paper
        elevation={0}
        variant="stats"
        sx={{
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: theme.palette.background.paper,
            '& .MuiTab-root': {
              py: 2
            }
          }}
        >
          <Tab
            icon={<NotificationsIcon />}
            label="My Notifications"
            iconPosition="start"
            id="alerts-tab-0"
            aria-controls="alerts-tabpanel-0"
          />
          <Tab
            icon={<ListAltIcon />}
            label="My Alerts"
            iconPosition="start"
            id="alerts-tab-1"
            aria-controls="alerts-tabpanel-1"
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box p={3}>
              <NotificationsList
                notifications={notifications}
                onMarkAsRead={handleMarkAsRead}
                onMarkAllAsRead={handleMarkAllAsRead}
                setNotifications={setNotifications}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
                onClearReadNotifications={handleClearReadNotifications}
              />
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box p={3}>
              <AlertsList
                alerts={alerts}
                onToggleAlert={handleAlertToggle}
                onDeleteAlert={handleAlertDelete}
                onCreateAlert={handleCreateAlert}
                onClearInactiveAlerts={handleClearInactiveAlerts}
              />
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* Error/Success Messages */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AlertManagementDashboard;